import { Check, Download, Paperclip, Pencil, Send, Trash2, Upload, X } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import { EditPcoLinkageDialog } from "@/components/change-orders/EditPcoLinkageDialog";
import { ReauthDialog } from "@/components/shared/ReauthDialog";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import {
  useChangeOrderAttachments,
  useDeleteChangeOrderAttachment,
  useUploadChangeOrderAttachment,
} from "@/hooks/use-attachments";
import { useProjectCostCodes } from "@/hooks/use-budgets";
import {
  type AppendTargetPayload,
  useAppendTargetsForChangeOrder,
  useApproveChangeOrder,
  useChangeOrder,
  useDeleteChangeOrder,
  useRejectChangeOrder,
  useSubmitChangeOrder,
  useVoidChangeOrder,
} from "@/hooks/use-change-orders";
import { ReauthRequiredError, useReauth } from "@/hooks/use-reauth";
import { authClient } from "@/lib/auth-client";
import { formatCurrency } from "@/lib/formatters";

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function PcoAttachmentsSection({
  changeOrderId,
  canEdit,
}: {
  changeOrderId: string;
  canEdit: boolean;
}) {
  const { data } = useChangeOrderAttachments(changeOrderId);
  const upload = useUploadChangeOrderAttachment(changeOrderId);
  const remove = useDeleteChangeOrderAttachment(changeOrderId);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const items = data ?? [];

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) {
      upload.mutate(file);
      e.target.value = "";
    }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-xs uppercase tracking-wider text-gray-500">
          <Paperclip className="inline h-3.5 w-3.5 mr-1" />
          Supporting Documents
        </h4>
        {canEdit && (
          <>
            <input type="file" ref={fileInputRef} className="hidden" onChange={handleFileChange} />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={upload.isPending}
            >
              <Upload className="h-3.5 w-3.5 mr-1.5" />
              {upload.isPending ? "Uploading…" : "Upload"}
            </Button>
          </>
        )}
      </div>
      {items.length === 0 ? (
        <p className="text-sm text-gray-500 italic py-2">No attachments.</p>
      ) : (
        <div className="space-y-2">
          {items.map((a) => (
            <div key={a.id} className="flex items-center gap-3 rounded-md border px-3 py-2 text-sm">
              <Paperclip className="h-4 w-4 shrink-0 text-gray-500" />
              <div className="min-w-0 flex-1">
                <p className="font-medium truncate">{a.fileName}</p>
                <p className="text-xs text-gray-500">
                  {formatFileSize(a.fileSize)}
                  {a.uploadedBy ? ` · ${a.uploadedBy}` : ""}
                  {a.uploadedDate ? ` · ${a.uploadedDate}` : ""}
                </p>
              </div>
              <a
                href={`/api/change-orders/${changeOrderId}/attachments/${a.id}`}
                download
                className="shrink-0"
              >
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <Download className="h-4 w-4" />
                </Button>
              </a>
              {canEdit && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-gray-500 hover:text-red-600"
                  onClick={() => remove.mutate(a.id)}
                  disabled={remove.isPending}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const CO_WRITE_USER_TYPES = new Set(["admin", "executive", "project_manager", "finance"]);
const CO_APPROVE_USER_TYPES = new Set(["admin", "executive", "finance"]);

function statusPillClasses(status: string): string {
  switch (status) {
    case "draft":
      return "bg-gray-100 text-gray-700";
    case "pending_approval":
      return "bg-amber-100 text-amber-800";
    case "executed":
      return "bg-green-100 text-green-700";
    case "voided":
      return "bg-red-100 text-red-700";
    case "terminated":
      return "bg-red-100 text-red-700";
    default:
      return "bg-gray-100 text-gray-700";
  }
}

function statusLabel(status: string): string {
  switch (status) {
    case "draft":
      return "Draft";
    case "pending_approval":
      return "Pending Approval";
    case "executed":
      return "Approved";
    case "voided":
      return "Voided";
    default:
      return status;
  }
}

export function PcoDetailDialog({
  changeOrderId,
  open,
  onOpenChange,
}: {
  changeOrderId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { data, isLoading } = useChangeOrder(changeOrderId);
  const { data: sessionData } = authClient.useSession();
  const submit = useSubmitChangeOrder();
  const approve = useApproveChangeOrder();
  const reauth = useReauth();
  const reject = useRejectChangeOrder();
  const voidCo = useVoidChangeOrder();
  const del = useDeleteChangeOrder();

  const [rejectReason, setRejectReason] = useState("");
  const [voidReason, setVoidReason] = useState("");
  const [editLinkageOpen, setEditLinkageOpen] = useState(false);
  const [mode, setMode] = useState<"detail" | "reject" | "void" | "approve_target">("detail");
  // Approval target picker state — only used for cost-code-linked PCOs.
  // "__new__" sentinel = mint a new commitment for the selected sub.
  const [pickedTarget, setPickedTarget] = useState<string>("");

  // Project's cost codes — looked up so the detail panel can display the
  // PCO's allocated cost code by name when linkage is `cost_code`.
  const { data: costCodes } = useProjectCostCodes(data?.projectId ?? "");
  const allocatedCostCode = useMemo(() => {
    if (!data || data.pcoLinkageType !== "cost_code" || !data.pcoCostCodeId) return null;
    const cc = (costCodes ?? []).find((c) => c.id === data.pcoCostCodeId);
    if (!cc) return null;
    return `${cc.code}${cc.description ? ` — ${cc.description}` : ""}`;
  }, [data, costCodes]);

  // Append-target options for cost-code-linked PCOs at approval time.
  const appendTargetsQuery = useAppendTargetsForChangeOrder(
    mode === "approve_target" ? changeOrderId : null,
  );

  const user = sessionData?.user as
    | { id?: string; role?: string; userType?: string | null }
    | undefined;
  const userType = user?.userType ?? null;
  const role = user?.role;
  const canWrite = useMemo(() => {
    if (userType) return CO_WRITE_USER_TYPES.has(userType);
    return role === "admin" || role === "project_manager";
  }, [userType, role]);
  const canApprove = useMemo(() => {
    if (userType) return CO_APPROVE_USER_TYPES.has(userType);
    return role === "admin";
  }, [userType, role]);

  const co = data;
  const isSubmitter = !!(user?.id && co?.submittedByUserId && user.id === co.submittedByUserId);

  function handleClose() {
    setRejectReason("");
    setVoidReason("");
    setPickedTarget("");
    setMode("detail");
    onOpenChange(false);
  }

  function runSubmit() {
    if (!co) return;
    submit.mutate(co.id, { onSuccess: handleClose });
  }

  // Approval guards (US-125 AC #9 and the buyout-pending block).
  const approveBlockReason = (() => {
    if (!co) return null;
    if (co.pcoLinkageType === "cost_code") {
      if (co.pcoRequiresBuyout) {
        return "PCO requires buyout. Complete buyout for the cost code, then re-edit the PCO to pick the now-committed sub before approving.";
      }
      if (!co.companyId) {
        return "PCO must have a sub assigned before approval.";
      }
    }
    return null;
  })();

  // Approve with automatic reauth retry. For COs > $10K the server returns
  // 401 reauthRequired; the hook surfaces ReauthRequiredError, which we catch
  // here to open the ReauthDialog and retry once the user has verified.
  function runApproveAttempt(coId: string, appendTarget?: AppendTargetPayload, token?: string) {
    approve.mutate(
      { id: coId, appendTarget, reauthToken: token },
      {
        onSuccess: handleClose,
        onError: (err) => {
          if (err instanceof ReauthRequiredError) {
            reauth.withReauth((verifiedToken) => {
              runApproveAttempt(coId, appendTarget, verifiedToken);
            });
          }
        },
      },
    );
  }

  function runApprove() {
    if (!co) return;
    // Commitment-linked PCOs use the parent commitment implicitly. Cost-code-
    // linked PCOs route through the append-target picker so the approver can
    // choose between the sub's existing commitments and minting a new one.
    if (co.pcoLinkageType === "cost_code" && !co.parentCommitmentId) {
      setPickedTarget("");
      setMode("approve_target");
      return;
    }
    runApproveAttempt(co.id);
  }

  function runApproveWithTarget() {
    if (!co || !pickedTarget) return;
    const appendTarget: AppendTargetPayload =
      pickedTarget === "__new__"
        ? { mode: "new" }
        : { mode: "existing", commitmentId: pickedTarget };
    runApproveAttempt(co.id, appendTarget);
  }
  function runReject() {
    if (!co) return;
    reject.mutate({ id: co.id, reason: rejectReason }, { onSuccess: handleClose });
  }
  function runVoid() {
    if (!co) return;
    voidCo.mutate({ id: co.id, reason: voidReason }, { onSuccess: handleClose });
  }
  function runDelete() {
    if (!co) return;
    del.mutate(co.id, { onSuccess: handleClose });
  }

  return (
    <>
      <ReauthDialog
        open={reauth.dialogOpen}
        onOpenChange={reauth.onDialogOpenChange}
        onVerified={reauth.onVerified}
      />
      {co?.pcoCostCodeId && (
        <EditPcoLinkageDialog
          open={editLinkageOpen}
          onOpenChange={setEditLinkageOpen}
          changeOrderId={co.id}
          projectId={co.projectId}
          costCodeId={co.pcoCostCodeId}
          costCodeLabel={allocatedCostCode ?? ""}
          currentCompanyId={co.companyId}
        />
      )}
      <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(true) : handleClose())}>
        <DialogContent className="w-[50vw] max-w-[50vw] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {co ? (
                <div className="flex items-center gap-3">
                  <span>{co.commitmentNumber}</span>
                  <span
                    className={`text-xs font-medium px-2 py-0.5 rounded-full ${statusPillClasses(co.status)}`}
                  >
                    {statusLabel(co.status)}
                  </span>
                </div>
              ) : (
                "Change Order"
              )}
            </DialogTitle>
          </DialogHeader>

          {isLoading || !co ? (
            <Skeleton className="h-48 w-full" />
          ) : mode === "reject" ? (
            <div className="space-y-3">
              <Label htmlFor="reject-reason">Rejection reason</Label>
              <Textarea
                id="reject-reason"
                rows={3}
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Explain why this PCO is being rejected…"
              />
              <DialogFooter>
                <Button variant="outline" onClick={() => setMode("detail")}>
                  Back
                </Button>
                <Button
                  variant="destructive"
                  disabled={!rejectReason.trim() || reject.isPending}
                  onClick={runReject}
                >
                  {reject.isPending ? "Rejecting…" : "Reject PCO"}
                </Button>
              </DialogFooter>
            </div>
          ) : mode === "approve_target" ? (
            <div className="space-y-3">
              <div>
                <h3 className="text-base font-semibold text-gray-900">Choose append target</h3>
                <p className="text-xs text-gray-500 mt-1">
                  Where should this PCO&apos;s line items land on approval?
                </p>
              </div>
              {appendTargetsQuery.isLoading ? (
                <Skeleton className="h-32 w-full" />
              ) : (
                <div className="space-y-2">
                  {(appendTargetsQuery.data ?? []).map((opt) => (
                    <label
                      key={opt.commitmentId}
                      className="flex items-start gap-2 rounded-md border px-3 py-2 cursor-pointer hover:bg-gray-50"
                    >
                      <input
                        type="radio"
                        name="append-target"
                        value={opt.commitmentId}
                        checked={pickedTarget === opt.commitmentId}
                        onChange={() => setPickedTarget(opt.commitmentId)}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <div className="text-sm font-medium">
                          Append to {opt.commitmentNumber} — {opt.commitmentTitle}
                        </div>
                        <div className="text-xs text-gray-500">
                          Revised {formatCurrency(opt.revisedAmount ?? "0", { precise: true })} ·{" "}
                          {opt.status}
                        </div>
                      </div>
                    </label>
                  ))}
                  <label className="flex items-start gap-2 rounded-md border px-3 py-2 cursor-pointer hover:bg-gray-50">
                    <input
                      type="radio"
                      name="append-target"
                      value="__new__"
                      checked={pickedTarget === "__new__"}
                      onChange={() => setPickedTarget("__new__")}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <div className="text-sm font-medium">Create new commitment</div>
                      <div className="text-xs text-gray-500">
                        Mint a new subcontract for this sub with the CO line items as its initial
                        Schedule of Values.
                      </div>
                    </div>
                  </label>
                </div>
              )}
              <DialogFooter>
                <Button variant="outline" onClick={() => setMode("detail")}>
                  Back
                </Button>
                <Button
                  onClick={runApproveWithTarget}
                  disabled={!pickedTarget || approve.isPending}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  <Check className="h-4 w-4 mr-1.5" />
                  {approve.isPending ? "Approving…" : "Approve"}
                </Button>
              </DialogFooter>
            </div>
          ) : mode === "void" ? (
            <div className="space-y-3">
              <Label htmlFor="void-reason">Void reason</Label>
              <Textarea
                id="void-reason"
                rows={3}
                value={voidReason}
                onChange={(e) => setVoidReason(e.target.value)}
                placeholder="Explain why this CO is being voided…"
              />
              <p className="text-xs text-amber-700">
                Voiding reverses the SOV roll-up on the parent commitment. Prior certified pay
                applications are not modified.
              </p>
              <DialogFooter>
                <Button variant="outline" onClick={() => setMode("detail")}>
                  Back
                </Button>
                <Button
                  variant="destructive"
                  disabled={!voidReason.trim() || voidCo.isPending}
                  onClick={runVoid}
                >
                  {voidCo.isPending ? "Voiding…" : "Void CO"}
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-5">
              <div>
                <h3 className="text-base font-semibold text-gray-900">{co.title}</h3>
                {co.description && <p className="text-sm text-gray-600 mt-1">{co.description}</p>}
              </div>

              <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
                <div>
                  <dt className="text-xs uppercase tracking-wider text-gray-500">Linkage</dt>
                  <dd className="text-gray-900">
                    {co.pcoLinkageType === "cost_code"
                      ? `Cost Code — ${allocatedCostCode ?? "—"}`
                      : co.parent
                        ? `Commitment — ${co.parent.commitmentNumber} — ${co.parent.title}`
                        : "—"}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs uppercase tracking-wider text-gray-500">Subcontractor</dt>
                  <dd className="text-gray-900">
                    {co.pcoRequiresBuyout ? (
                      <span className="inline-flex items-center text-xs font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
                        Buyout pending
                      </span>
                    ) : (
                      (co.companyName ?? co.parent?.companyName ?? "—")
                    )}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs uppercase tracking-wider text-gray-500">Total</dt>
                  <dd className="text-gray-900 font-medium">
                    {formatCurrency(co.originalAmount, { precise: true })}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs uppercase tracking-wider text-gray-500">
                    Schedule Impact
                  </dt>
                  <dd className="text-gray-900">
                    {co.scheduleImpactDays != null ? `${co.scheduleImpactDays} days` : "—"}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs uppercase tracking-wider text-gray-500">
                    Client Deadline
                  </dt>
                  <dd className="text-gray-900">{co.clientDecisionDeadline ?? "—"}</dd>
                </div>
                <div>
                  <dt className="text-xs uppercase tracking-wider text-gray-500">
                    Retention Override
                  </dt>
                  <dd className="text-gray-900">
                    {co.retentionPercentOverride != null
                      ? `${co.retentionPercentOverride}%`
                      : "Inherits parent"}
                  </dd>
                </div>
                {co.rejectionReason && (
                  <div className="col-span-2">
                    <dt className="text-xs uppercase tracking-wider text-red-600">
                      Previous Rejection
                    </dt>
                    <dd className="text-gray-900">{co.rejectionReason}</dd>
                  </div>
                )}
                {co.voidReason && (
                  <div className="col-span-2">
                    <dt className="text-xs uppercase tracking-wider text-red-600">Void Reason</dt>
                    <dd className="text-gray-900">{co.voidReason}</dd>
                    {co.reconciliationFlag && (
                      <p className="text-xs text-amber-700 mt-1">
                        Reconciliation required — billing recorded before void.
                      </p>
                    )}
                  </div>
                )}
              </dl>

              <div className="rounded-md border overflow-hidden">
                <Table>
                  <TableHeader className="bg-gray-50">
                    <TableRow>
                      <TableHead className="text-xs uppercase tracking-wider text-gray-500">
                        #
                      </TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-gray-500">
                        Description
                      </TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-gray-500 text-right">
                        Amount
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {co.lineItems.map((li) => (
                      <TableRow key={li.id}>
                        <TableCell className="text-sm">{li.itemNo}</TableCell>
                        <TableCell className="text-sm">{li.description}</TableCell>
                        <TableCell className="text-sm text-right">
                          {formatCurrency(li.scheduledValue, { precise: true })}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <PcoAttachmentsSection
                changeOrderId={co.id}
                canEdit={canWrite && (co.status === "draft" || co.status === "pending_approval")}
              />

              {co.auditTrail.length > 0 && (
                <div>
                  <h4 className="text-xs uppercase tracking-wider text-gray-500 mb-2">
                    Status History
                  </h4>
                  <ul className="space-y-1 text-sm text-gray-700">
                    {co.auditTrail.map((entry) => (
                      <li key={entry.id} className="flex justify-between border-b py-1">
                        <span>{entry.action}</span>
                        <span className="text-gray-500">
                          {new Date(entry.createdAt).toLocaleString()}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <DialogFooter className="flex-wrap gap-2">
                {co.status === "draft" && canWrite && (
                  <>
                    <Button variant="outline" onClick={runDelete} disabled={del.isPending}>
                      <Trash2 className="h-4 w-4 mr-1.5" />
                      Delete
                    </Button>
                    {co.pcoLinkageType === "cost_code" && co.pcoCostCodeId && (
                      <Button variant="outline" onClick={() => setEditLinkageOpen(true)}>
                        <Pencil className="h-4 w-4 mr-1.5" />
                        Edit Linkage
                      </Button>
                    )}
                    <Button
                      onClick={runSubmit}
                      disabled={submit.isPending}
                      className="bg-[#334155] hover:bg-[#415A77] text-white"
                    >
                      <Send className="h-4 w-4 mr-1.5" />
                      {submit.isPending ? "Submitting…" : "Submit for Approval"}
                    </Button>
                  </>
                )}
                {co.status === "pending_approval" && canApprove && (
                  <>
                    <Button
                      variant="outline"
                      onClick={() => setMode("reject")}
                      disabled={reject.isPending}
                    >
                      <X className="h-4 w-4 mr-1.5" />
                      Reject
                    </Button>
                    <Button
                      onClick={runApprove}
                      disabled={approve.isPending || isSubmitter || approveBlockReason !== null}
                      title={
                        approveBlockReason ??
                        (isSubmitter ? "A user cannot approve their own submission" : undefined)
                      }
                      className="bg-green-600 hover:bg-green-700 text-white"
                    >
                      <Check className="h-4 w-4 mr-1.5" />
                      {approve.isPending ? "Approving…" : "Approve"}
                    </Button>
                  </>
                )}
                {co.status === "executed" && canApprove && (
                  <Button
                    variant="destructive"
                    onClick={() => setMode("void")}
                    disabled={voidCo.isPending}
                  >
                    Void CO
                  </Button>
                )}
                <Button variant="outline" onClick={handleClose}>
                  Close
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
